import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search as SearchIcon, ArrowRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatList = (val) => (Array.isArray(val) ? val.join(', ') : (val || ''));

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setQuery(q);
    if (q.trim()) {
      runSearch(q);
    } else {
      setResults([]);
      setHasSearched(false);
    }
  }, [searchParams]);

  const runSearch = async (q) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      params.append('search', q);
      const res = await axios.get(`${API}/cases?${params}`);
      setResults(res.data || []);
    } catch {
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  const handleCaseClick = (caseId) => {
    if (user) {
      // Logged-in users go straight to the case (server still enforces subscription)
      navigate(`/cases/${caseId}`);
    } else {
      // Anonymous: send to login with redirect intent
      navigate(`/login?redirect=${encodeURIComponent(`/cases/${caseId}`)}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-serif font-bold tracking-tight text-slate-900 mb-2" data-testid="search-page-title">
            Search the catalogue
          </h1>
          <p className="text-slate-600 mb-6">
            Browse case study titles, authors, and topics. Sign in to read full content.
          </p>

          <form onSubmit={handleSubmit} className="relative mb-8" data-testid="search-form">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter Keywords, Titles, Concepts"
              className="w-full pl-12 pr-32 py-4 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white shadow-sm"
              data-testid="search-input"
              autoFocus
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-medium text-sm"
              data-testid="search-submit-button"
            >
              Search
            </button>
          </form>

          {loading ? (
            <div className="flex justify-center py-16" data-testid="search-loading">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl" data-testid="search-empty-state">
              <Filter className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-700 font-medium">Start by typing a keyword above.</p>
              <p className="text-slate-500 text-sm mt-2">Try &quot;Marketing&quot;, &quot;Finance&quot;, &quot;Leadership&quot;, or any concept name.</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-xl" data-testid="search-no-results">
              <p className="text-slate-700 font-medium">No cases found for <em>&quot;{query}&quot;</em>.</p>
              <p className="text-slate-500 text-sm mt-2">Try a different keyword or browse our subscriber categories.</p>
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-600 mb-4" data-testid="search-results-count">
                Showing <strong>{results.length}</strong> case{results.length === 1 ? '' : 's'} matching <em>&quot;{query}&quot;</em>
              </div>
              <div className="space-y-4">
                {results.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleCaseClick(c.id)}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-900 hover:shadow-md transition-all group"
                    data-testid={`search-result-${c.id}`}
                  >
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formatList(c.product_types) && (
                        <span className="inline-block px-2.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-mono rounded">
                          {formatList(c.product_types)}
                        </span>
                      )}
                      {formatList(c.domain) && (
                        <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                          {formatList(c.domain)}
                        </span>
                      )}
                      {c.geography && (
                        <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                          {c.geography}
                        </span>
                      )}
                      {c.year && (
                        <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                          {c.year}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-slate-900 group-hover:text-orange-700 transition-colors mb-2" data-testid={`search-result-title-${c.id}`}>
                      {c.title}
                    </h3>
                    {c.description && (
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{c.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      {c.author && <span>By <strong className="text-slate-700">{c.author}</strong></span>}
                      {c.length_pages && <span>• {c.length_pages} pages</span>}
                      {formatList(c.level) && <span>• {formatList(c.level)}</span>}
                      <span className="ml-auto inline-flex items-center gap-1 text-orange-600 font-medium group-hover:gap-2 transition-all">
                        {user ? 'Read case' : 'Sign in to read'} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
